import React, { useState, useEffect, useCallback } from 'react';
import { cases } from '../../services/api';

const EVIDENCE_TYPES = [
  { value: 'document', label: 'Document' },
  { value: 'intelligence', label: 'Intel·ligència' },
  { value: 'osint', label: 'OSINT' },
  { value: 'humint', label: 'HUMINT' },
  { value: 'sigint', label: 'SIGINT' },
  { value: 'media', label: 'Mitjans' },
  { value: 'technical', label: 'Tècnic' }
];

const RELIABILITY_SOURCE = [
  { value: 'A', label: 'A - Completament fiable' },
  { value: 'B', label: 'B - Normalment fiable' },
  { value: 'C', label: 'C - Bastant fiable' },
  { value: 'D', label: 'D - Normalment no fiable' },
  { value: 'E', label: 'E - No fiable' },
  { value: 'F', label: 'F - Fiabilitat desconeguda' }
];

const RELIABILITY_INFO = [
  { value: '1', label: '1 - Confirmada' },
  { value: '2', label: '2 - Probablement certa' },
  { value: '3', label: '3 - Possiblement certa' },
  { value: '4', label: '4 - Dubtosa' },
  { value: '5', label: '5 - Improbable' },
  { value: '6', label: '6 - No avaluable' }
];

const CLASSIFICATION = [
  { value: 'public', label: 'Públic' },
  { value: 'internal', label: 'Intern' },
  { value: 'confidential', label: 'Confidencial' },
  { value: 'secret', label: 'Secret' }
];

const ACTOR_TYPES = [
  { value: 'state', label: 'Estatal' },
  { value: 'non-state', label: 'No estatal' },
  { value: 'individual', label: 'Individual' },
  { value: 'group', label: 'Grup' },
  { value: 'unknown', label: 'Desconegut' }
];

function CaseContentManager({ caseId, caseData, onClose, onCaseUpdate }) {
  const [activeTab, setActiveTab] = useState('briefing');
  const [evidenceList, setEvidenceList] = useState([]);
  const [actorTemplates, setActorTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Briefing form state
  const [briefingForm, setBriefingForm] = useState({
    briefingContent: caseData?.briefingContent || '',
    instructions: caseData?.instructions || ''
  });
  const [savingBriefing, setSavingBriefing] = useState(false);

  // Configuració del cas.
  //
  // Fins al 07/08/2026 un cas es podia crear (formulari de ProjectDetailPage) però no
  // editar: l'única crida a cases.update enviava el briefing i les instruccions. Ni la
  // durada, ni la dificultat, ni el títol, ni si era d'equip es podien canviar mai més.
  // L'API sempre ho ha permès —updateCaseSchema cobreix tots aquests camps, i
  // timeLimitMinutes accepta null per a temps indefinit—; només faltava exposar-ho.
  const [configForm, setConfigForm] = useState({
    title: caseData?.title || '',
    description: caseData?.description || '',
    difficulty: caseData?.difficulty || 'intermediate',
    isTeamBased: !!caseData?.isTeamBased,
    timeLimitMinutes: caseData?.timeLimitMinutes ? String(caseData.timeLimitMinutes) : ''
  });
  const [savingConfig, setSavingConfig] = useState(false);

  // Evidence form state
  const [showEvidenceForm, setShowEvidenceForm] = useState(false);
  const [editingEvidence, setEditingEvidence] = useState(null);
  const [evidenceForm, setEvidenceForm] = useState({
    code: '',
    title: '',
    content: '',
    source: '',
    evidenceType: '',
    reliabilitySource: '',
    reliabilityInfo: '',
    classification: 'internal',
    orderIndex: 0,
    releaseDelayMinutes: '',
    isReleased: true
  });
  const [submittingEvidence, setSubmittingEvidence] = useState(false);

  // Actor template form state
  const [showActorForm, setShowActorForm] = useState(false);
  const [editingActor, setEditingActor] = useState(null);
  const [actorForm, setActorForm] = useState({
    name: '',
    description: '',
    actorType: 'unknown',
    orderIndex: 0
  });
  const [submittingActor, setSubmittingActor] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [evidenceRes, actorsRes] = await Promise.all([
        cases.getEvidence(caseId),
        cases.getActorTemplates(caseId)
      ]);
      setEvidenceList(evidenceRes.data);
      setActorTemplates(actorsRes.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Error carregant dades');
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Config handlers
  const handleSaveConfig = async (e) => {
    e.preventDefault();
    if (!configForm.title.trim()) {
      setError('El títol és obligatori');
      return;
    }
    try {
      setSavingConfig(true);
      setError('');
      setSuccessMessage('');

      // El camp buit vol dir temps indefinit, i s'envia com a null explícit. El
      // servidor ja no fa COALESCE, així que un null aquí esborra el límit de debò.
      await cases.update(caseId, {
        title: configForm.title.trim(),
        description: configForm.description,
        difficulty: configForm.difficulty,
        isTeamBased: configForm.isTeamBased,
        timeLimitMinutes: configForm.timeLimitMinutes === ''
          ? null
          : parseInt(configForm.timeLimitMinutes, 10)
      });

      setSuccessMessage('Configuració guardada correctament');
      if (onCaseUpdate) onCaseUpdate();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Error guardant la configuració');
    } finally {
      setSavingConfig(false);
    }
  };

  // Evidence handlers
  const handleOpenEvidenceCreate = () => {
    setEditingEvidence(null);
    setEvidenceForm({
      code: `E${(evidenceList.length + 1).toString().padStart(3, '0')}`,
      title: '',
      content: '',
      source: '',
      evidenceType: 'document',
      reliabilitySource: '',
      reliabilityInfo: '',
      classification: 'internal',
      orderIndex: evidenceList.length,
      releaseDelayMinutes: '',
      isReleased: true
    });
    setShowEvidenceForm(true);
  };

  const handleOpenEvidenceEdit = (evidence) => {
    setEditingEvidence(evidence);
    setEvidenceForm({
      code: evidence.code,
      title: evidence.title,
      content: evidence.content || '',
      source: evidence.source || '',
      evidenceType: evidence.evidenceType || 'document',
      reliabilitySource: evidence.reliabilitySource || '',
      reliabilityInfo: evidence.reliabilityInfo || '',
      classification: evidence.classification || 'internal',
      orderIndex: evidence.orderIndex || 0,
      releaseDelayMinutes: evidence.releaseDelayMinutes || '',
      isReleased: evidence.isReleased !== false
    });
    setShowEvidenceForm(true);
  };

  const handleEvidenceSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmittingEvidence(true);
      setError('');

      const data = {
        ...evidenceForm,
        releaseDelayMinutes: evidenceForm.releaseDelayMinutes ? parseInt(evidenceForm.releaseDelayMinutes) : null,
        orderIndex: parseInt(evidenceForm.orderIndex) || 0
      };

      if (editingEvidence) {
        await cases.updateEvidence(caseId, editingEvidence.id, data);
      } else {
        await cases.createEvidence(caseId, data);
      }

      setShowEvidenceForm(false);
      setEditingEvidence(null);
      loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Error guardant evidència');
    } finally {
      setSubmittingEvidence(false);
    }
  };

  const handleDeleteEvidence = async (evidenceId) => {
    if (!window.confirm('Segur que vols eliminar aquesta evidència?')) return;
    try {
      setError('');
      await cases.deleteEvidence(caseId, evidenceId);
      loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Error eliminant evidència');
    }
  };

  // Actor template handlers
  const handleOpenActorCreate = () => {
    setEditingActor(null);
    setActorForm({
      name: '',
      description: '',
      actorType: 'unknown',
      orderIndex: actorTemplates.length
    });
    setShowActorForm(true);
  };

  const handleOpenActorEdit = (actor) => {
    setEditingActor(actor);
    setActorForm({
      name: actor.name,
      description: actor.description || '',
      actorType: actor.actorType || 'unknown',
      orderIndex: actor.orderIndex || 0
    });
    setShowActorForm(true);
  };

  const handleActorSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmittingActor(true);
      setError('');

      if (editingActor) {
        await cases.updateActorTemplate(caseId, editingActor.id, actorForm);
      } else {
        await cases.createActorTemplate(caseId, actorForm);
      }

      setShowActorForm(false);
      setEditingActor(null);
      loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Error guardant actor');
    } finally {
      setSubmittingActor(false);
    }
  };

  const handleDeleteActor = async (actorId) => {
    if (!window.confirm('Segur que vols eliminar aquest actor predefinit?')) return;
    try {
      setError('');
      await cases.deleteActorTemplate(caseId, actorId);
      loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Error eliminant actor');
    }
  };

  // Briefing handlers
  const handleSaveBriefing = async (e) => {
    e.preventDefault();
    try {
      setSavingBriefing(true);
      setError('');
      setSuccessMessage('');

      await cases.update(caseId, {
        briefingContent: briefingForm.briefingContent,
        instructions: briefingForm.instructions
      });

      setSuccessMessage('Contingut guardat correctament');

      // Notify parent to refresh case data
      if (onCaseUpdate) {
        onCaseUpdate();
      }

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Error guardant el briefing');
    } finally {
      setSavingBriefing(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-white rounded-lg p-6">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-surface rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between">
          <h2 className="text-xl font-semibold text-content">Contingut del Cas</h2>
          <button onClick={onClose} className="text-content-muted hover:text-content-secondary">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b">
          <div className="flex">
            <button
              onClick={() => setActiveTab('config')}
              className={`px-4 py-2 font-medium ${
                activeTab === 'config'
                  ? 'text-accent border-b-2 border-primary-600'
                  : 'text-content-muted hover:text-content-secondary'
              }`}
            >
              Configuració
            </button>
            <button
              onClick={() => setActiveTab('briefing')}
              className={`px-4 py-2 font-medium ${
                activeTab === 'briefing'
                  ? 'text-accent border-b-2 border-primary-600'
                  : 'text-content-muted hover:text-content-secondary'
              }`}
            >
              Briefing
            </button>
            <button
              onClick={() => setActiveTab('evidence')}
              className={`px-4 py-2 font-medium ${
                activeTab === 'evidence'
                  ? 'text-accent border-b-2 border-primary-600'
                  : 'text-content-muted hover:text-content-secondary'
              }`}
            >
              Evidencies ({evidenceList.length})
            </button>
            <button
              onClick={() => setActiveTab('actors')}
              className={`px-4 py-2 font-medium ${
                activeTab === 'actors'
                  ? 'text-accent border-b-2 border-primary-600'
                  : 'text-content-muted hover:text-content-secondary'
              }`}
            >
              Actors predefinits ({actorTemplates.length})
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {error && (
            <div className="mb-4 bg-error-subtle border border-error/30 text-error-text px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {successMessage && (
            <div className="mb-4 bg-success-subtle border border-success/30 text-success-text px-4 py-3 rounded-lg flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              {successMessage}
            </div>
          )}

          {/* Configuració del cas */}
          {activeTab === 'config' && (
            <form onSubmit={handleSaveConfig} className="space-y-4 max-w-2xl">
              <div>
                <label className="block text-sm font-medium text-content-secondary mb-1">
                  Títol del cas
                </label>
                <input
                  type="text"
                  value={configForm.title}
                  onChange={(e) => setConfigForm({ ...configForm, title: e.target.value })}
                  className="input w-full"
                  maxLength={255}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-content-secondary mb-1">
                  Descripció
                </label>
                <textarea
                  value={configForm.description}
                  onChange={(e) => setConfigForm({ ...configForm, description: e.target.value })}
                  className="input w-full"
                  rows={3}
                  maxLength={2000}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-content-secondary mb-1">
                    Dificultat
                  </label>
                  <select
                    value={configForm.difficulty}
                    onChange={(e) => setConfigForm({ ...configForm, difficulty: e.target.value })}
                    className="input w-full"
                  >
                    <option value="beginner">Principiant</option>
                    <option value="intermediate">Intermedi</option>
                    <option value="advanced">Avançat</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-content-secondary mb-1">
                    Temps límit (minuts)
                  </label>
                  <input
                    type="number"
                    min="1"
                    placeholder="Deixa-ho buit per a temps indefinit"
                    value={configForm.timeLimitMinutes}
                    onChange={(e) => setConfigForm({ ...configForm, timeLimitMinutes: e.target.value })}
                    className="input w-full"
                  />
                  <p className="mt-1 text-xs text-content-muted">
                    {configForm.timeLimitMinutes === ''
                      ? 'Sense límit: no hi haurà compte enrere i l\'exercici no es tancarà sol.'
                      : `Equival a ${Math.floor(parseInt(configForm.timeLimitMinutes, 10) / 60) || 0} h ${parseInt(configForm.timeLimitMinutes, 10) % 60 || 0} min.`}
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="configIsTeamBased"
                  checked={configForm.isTeamBased}
                  onChange={(e) => setConfigForm({ ...configForm, isTeamBased: e.target.checked })}
                  className="mt-1 h-4 w-4"
                />
                <label htmlFor="configIsTeamBased" className="ml-2 text-sm text-content-secondary">
                  Exercici d'equip
                  <span className="block text-xs text-content-muted">
                    Els membres d'un equip comparteixen un sol exercici: mateixos actors, mateixa
                    matriu ACH, mateix rellotge i una sola nota. Cal crear els equips i inscriure'ls
                    des de «Equips» i «Inscripcions».
                  </span>
                </label>
              </div>

              <div className="bg-warning-subtle border border-warning/30 rounded-lg p-3 text-sm text-warning-text">
                Canviar el temps límit afecta els exercicis <strong>en curs</strong>: el compte enrere
                es recalcula sobre l'hora d'inici, que no es toca. Escurçar-lo pot tancar-ne algun de
                seguida; allargar-lo dona més marge. Canviar «exercici d'equip» amb inscripcions ja
                fetes deixarà les que ja no encaixin amb el tipus nou.
              </div>

              <div className="flex justify-end">
                <button type="submit" disabled={savingConfig} className="btn btn-primary disabled:opacity-50">
                  {savingConfig ? 'Guardant...' : 'Guardar configuració'}
                </button>
              </div>
            </form>
          )}

          {/* Briefing Tab */}
          {activeTab === 'briefing' && (
            <form onSubmit={handleSaveBriefing} className="space-y-6">
              <div className="bg-info-subtle border border-info/30 rounded-lg p-4 mb-4">
                <p className="text-sm text-info-text">
                  El contingut del briefing i les instruccions suporten <strong>Markdown</strong>.
                  Pots utilitzar taules, capçaleres, llistes, codi, etc.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-content-secondary mb-2">
                  <span className="flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                    Contingut del Briefing
                  </span>
                </label>
                <p className="text-xs text-content-muted mb-2">
                  Narrativa immersiva que els analistes veuran al iniciar el cas. Explica el context, la situació, i proporciona el rerefons necessari.
                </p>
                <textarea
                  rows={15}
                  value={briefingForm.briefingContent}
                  onChange={(e) => setBriefingForm({ ...briefingForm, briefingContent: e.target.value })}
                  className="input w-full font-mono text-sm"
                  placeholder="# Títol del Briefing&#10;&#10;## Context&#10;&#10;Descripció del context...&#10;&#10;## Situació Actual&#10;&#10;Explicació de la situació..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-content-secondary mb-2">
                  <span className="flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                    </svg>
                    Instruccions de l'Exercici
                  </span>
                </label>
                <p className="text-xs text-content-muted mb-2">
                  Directrius concretes sobre les tasques que han de realitzar els analistes (identificar actors, avaluar amenaces, etc.).
                </p>
                <textarea
                  rows={8}
                  value={briefingForm.instructions}
                  onChange={(e) => setBriefingForm({ ...briefingForm, instructions: e.target.value })}
                  className="input w-full font-mono text-sm"
                  placeholder="## Tasques&#10;&#10;1. Identificar els actors principals&#10;2. Avaluar el nivell d'amenaça&#10;3. Elaborar hipòtesis amb ACH"
                />
              </div>

              <div className="flex justify-end pt-4 border-t">
                <button
                  type="submit"
                  disabled={savingBriefing}
                  className="btn btn-primary disabled:opacity-50"
                >
                  {savingBriefing ? 'Guardant...' : 'Guardar canvis'}
                </button>
              </div>
            </form>
          )}

          {/* Evidence Tab */}
          {activeTab === 'evidence' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <p className="text-sm text-content-secondary">
                  Les evidències són els documents i informacions que els analistes rebran per treballar el cas.
                </p>
                <button onClick={handleOpenEvidenceCreate} className="btn btn-primary">
                  Afegir evidència
                </button>
              </div>

              {evidenceList.length === 0 ? (
                <div className="text-center py-8 bg-warning-subtle border border-warning/30 rounded-lg">
                  <p className="text-warning-text">
                    No hi ha evidències. Cal afegir almenys una evidència per poder publicar el cas.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {evidenceList.map((evidence) => (
                    <div key={evidence.id} className="border rounded-lg p-4 bg-surface">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <code className="bg-surface-muted px-2 py-0.5 rounded text-sm font-mono text-content">
                              {evidence.code}
                            </code>
                            <h3 className="font-medium text-content">{evidence.title}</h3>
                            {!evidence.isReleased && (
                              <span className="px-2 py-0.5 text-xs bg-warning-subtle text-warning-text rounded">
                                Retardada
                              </span>
                            )}
                          </div>
                          <div className="mt-1 flex flex-wrap gap-2 text-xs">
                            {evidence.evidenceType && (
                              <span className="px-2 py-0.5 bg-accent-subtle text-accent-hover rounded">
                                {EVIDENCE_TYPES.find(t => t.value === evidence.evidenceType)?.label}
                              </span>
                            )}
                            {evidence.classification && (
                              <span className="px-2 py-0.5 bg-surface-muted text-content-secondary rounded">
                                {CLASSIFICATION.find(c => c.value === evidence.classification)?.label}
                              </span>
                            )}
                            {evidence.reliabilitySource && evidence.reliabilityInfo && (
                              <span className="px-2 py-0.5 bg-info-subtle text-info-text rounded">
                                {evidence.reliabilitySource}{evidence.reliabilityInfo}
                              </span>
                            )}
                            {evidence.releaseDelayMinutes && (
                              <span className="px-2 py-0.5 bg-warning-subtle text-warning-text rounded">
                                Alliberar després de {evidence.releaseDelayMinutes} min
                              </span>
                            )}
                          </div>
                          {evidence.content && (
                            <p className="mt-2 text-sm text-content-secondary line-clamp-2">
                              {evidence.content}
                            </p>
                          )}
                        </div>
                        <div className="flex space-x-2 ml-4">
                          <button
                            onClick={() => handleOpenEvidenceEdit(evidence)}
                            className="text-accent hover:text-accent text-sm"
                          >
                            Editar
                          </button>
                          <button
                            onClick={() => handleDeleteEvidence(evidence.id)}
                            className="text-error hover:text-error-text text-sm"
                          >
                            Eliminar
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Actors Tab */}
          {activeTab === 'actors' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <p className="text-sm text-content-secondary">
                  Els actors predefinits són suggeriments opcionals que apareixeran als analistes com a punt de partida.
                </p>
                <button onClick={handleOpenActorCreate} className="btn btn-primary">
                  Afegir actor
                </button>
              </div>

              {actorTemplates.length === 0 ? (
                <div className="text-center py-8 bg-surface-subtle border border-border rounded-lg">
                  <p className="text-content-muted">
                    No hi ha actors predefinits. Això és opcional.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {actorTemplates.map((actor) => (
                    <div key={actor.id} className="border rounded-lg p-4 bg-surface">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <h3 className="font-medium text-content">{actor.name}</h3>
                            <span className="px-2 py-0.5 text-xs bg-accent-subtle text-accent-hover rounded">
                              {ACTOR_TYPES.find(t => t.value === actor.actorType)?.label}
                            </span>
                          </div>
                          {actor.description && (
                            <p className="mt-1 text-sm text-content-secondary">
                              {actor.description}
                            </p>
                          )}
                        </div>
                        <div className="flex space-x-2 ml-4">
                          <button
                            onClick={() => handleOpenActorEdit(actor)}
                            className="text-accent hover:text-accent text-sm"
                          >
                            Editar
                          </button>
                          <button
                            onClick={() => handleDeleteActor(actor.id)}
                            className="text-error hover:text-error-text text-sm"
                          >
                            Eliminar
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t flex justify-end">
          <button onClick={onClose} className="btn btn-secondary">
            Tancar
          </button>
        </div>
      </div>

      {/* Evidence Form Modal */}
      {showEvidenceForm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-lg font-semibold mb-4">
                {editingEvidence ? 'Editar evidència' : 'Nova evidència'}
              </h3>
              <form onSubmit={handleEvidenceSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-content-secondary">Codi *</label>
                    <input
                      type="text"
                      required
                      maxLength={10}
                      value={evidenceForm.code}
                      onChange={(e) => setEvidenceForm({ ...evidenceForm, code: e.target.value.toUpperCase() })}
                      placeholder="E001"
                      className="input mt-1 w-full font-mono"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-content-secondary">Tipus</label>
                    <select
                      value={evidenceForm.evidenceType}
                      onChange={(e) => setEvidenceForm({ ...evidenceForm, evidenceType: e.target.value })}
                      className="input mt-1 w-full"
                    >
                      <option value="">Selecciona...</option>
                      {EVIDENCE_TYPES.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-content-secondary">Títol *</label>
                  <input
                    type="text"
                    required
                    value={evidenceForm.title}
                    onChange={(e) => setEvidenceForm({ ...evidenceForm, title: e.target.value })}
                    className="input mt-1 w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-content-secondary">Contingut</label>
                  <textarea
                    rows={6}
                    value={evidenceForm.content}
                    onChange={(e) => setEvidenceForm({ ...evidenceForm, content: e.target.value })}
                    className="input mt-1 w-full"
                    placeholder="Contingut de l'evidència..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-content-secondary">Font</label>
                  <input
                    type="text"
                    value={evidenceForm.source}
                    onChange={(e) => setEvidenceForm({ ...evidenceForm, source: e.target.value })}
                    className="input mt-1 w-full"
                    placeholder="D'on prové aquesta informació"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-content-secondary">Fiabilitat de la font</label>
                    <select
                      value={evidenceForm.reliabilitySource}
                      onChange={(e) => setEvidenceForm({ ...evidenceForm, reliabilitySource: e.target.value })}
                      className="input mt-1 w-full"
                    >
                      <option value="">Selecciona...</option>
                      {RELIABILITY_SOURCE.map(r => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-content-secondary">Fiabilitat de la info</label>
                    <select
                      value={evidenceForm.reliabilityInfo}
                      onChange={(e) => setEvidenceForm({ ...evidenceForm, reliabilityInfo: e.target.value })}
                      className="input mt-1 w-full"
                    >
                      <option value="">Selecciona...</option>
                      {RELIABILITY_INFO.map(r => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-content-secondary">Classificació</label>
                    <select
                      value={evidenceForm.classification}
                      onChange={(e) => setEvidenceForm({ ...evidenceForm, classification: e.target.value })}
                      className="input mt-1 w-full"
                    >
                      {CLASSIFICATION.map(c => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-content-secondary">Ordre</label>
                    <input
                      type="number"
                      min={0}
                      value={evidenceForm.orderIndex}
                      onChange={(e) => setEvidenceForm({ ...evidenceForm, orderIndex: e.target.value })}
                      className="input mt-1 w-full"
                    />
                  </div>
                </div>

                {/* Release timing */}
                <div className="border-t pt-4">
                  <h4 className="font-medium text-content mb-2">Alliberament temporitzat (opcional)</h4>
                  <p className="text-sm text-content-muted mb-3">
                    Pots configurar que aquesta evidència s'alliberi automàticament després d'un temps des de l'inici de l'assessment.
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-content-secondary">Retard (minuts)</label>
                      <input
                        type="number"
                        min={0}
                        value={evidenceForm.releaseDelayMinutes}
                        onChange={(e) => setEvidenceForm({
                          ...evidenceForm,
                          releaseDelayMinutes: e.target.value,
                          isReleased: !e.target.value
                        })}
                        className="input mt-1 w-full"
                        placeholder="Deixa buit per alliberar immediatament"
                      />
                    </div>
                    <div className="flex items-end">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={evidenceForm.isReleased}
                          onChange={(e) => setEvidenceForm({ ...evidenceForm, isReleased: e.target.checked })}
                          className="h-4 w-4 text-primary-600 rounded"
                          disabled={!!evidenceForm.releaseDelayMinutes}
                        />
                        <span className="ml-2 text-sm text-content-secondary">Disponible immediatament</span>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowEvidenceForm(false)}
                    className="btn btn-secondary"
                  >
                    Cancel·lar
                  </button>
                  <button
                    type="submit"
                    disabled={submittingEvidence}
                    className="btn btn-primary disabled:opacity-50"
                  >
                    {submittingEvidence ? 'Guardant...' : (editingEvidence ? 'Actualitzar' : 'Crear')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Actor Form Modal */}
      {showActorForm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold mb-4">
                {editingActor ? 'Editar actor predefinit' : 'Nou actor predefinit'}
              </h3>
              <form onSubmit={handleActorSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-content-secondary">Nom *</label>
                  <input
                    type="text"
                    required
                    value={actorForm.name}
                    onChange={(e) => setActorForm({ ...actorForm, name: e.target.value })}
                    className="input mt-1 w-full"
                    placeholder="Nom de l'actor"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-content-secondary">Tipus</label>
                  <select
                    value={actorForm.actorType}
                    onChange={(e) => setActorForm({ ...actorForm, actorType: e.target.value })}
                    className="input mt-1 w-full"
                  >
                    {ACTOR_TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-content-secondary">Descripció</label>
                  <textarea
                    rows={3}
                    value={actorForm.description}
                    onChange={(e) => setActorForm({ ...actorForm, description: e.target.value })}
                    className="input mt-1 w-full"
                    placeholder="Descripció opcional de l'actor..."
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowActorForm(false)}
                    className="btn btn-secondary"
                  >
                    Cancel·lar
                  </button>
                  <button
                    type="submit"
                    disabled={submittingActor}
                    className="btn btn-primary disabled:opacity-50"
                  >
                    {submittingActor ? 'Guardant...' : (editingActor ? 'Actualitzar' : 'Crear')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CaseContentManager;
